/**
 * KPI aggregation for live + post-campaign reporting.
 *
 * The web client maintains a flat array of message + response events; these
 * functions reduce them into the metrics shown on the dashboards. In a server
 * deployment, swap the in-memory reducer for a SQL materialized view query.
 */

export type DeliveryStatus = "queued" | "sent" | "delivered" | "failed" | "undelivered";

export interface MessageEvent {
  id: string;
  campaignId: string;
  contactId: string;
  channel: "sms" | "whatsapp";
  language: "en" | "es";
  direction: "outbound" | "inbound";
  status?: DeliveryStatus;
  body: string;
  flowNodeId?: string;
  at: string; // ISO
  errorCode?: string;
  optOut?: boolean;
  answerKey?: string;
  answerValue?: string;
}

export interface CampaignKPIs {
  campaignId: string;
  totals: {
    queued: number;
    sent: number;
    delivered: number;
    failed: number;
    undelivered: number;
    inbound: number;
    optOuts: number;
  };
  rates: {
    deliveryRate: number;       // delivered / (sent + delivered + failed + undelivered)
    responseRate: number;       // unique inbound senders / delivered
    optOutRate: number;         // optOuts / delivered
    completionRate: number;     // completed conversations / started
  };
  byLanguage: Record<"en" | "es", { sent: number; delivered: number; inbound: number; optOuts: number }>;
  answerDistribution: Record<string, Record<string, number>>; // answerKey -> value -> count
  flowDropOff: Record<string, number>; // nodeId -> count of conversations that stopped here
}

export function computeKPIs(
  events: MessageEvent[],
  args: { campaignId: string; startedConversations: number; completedConversations: number },
): CampaignKPIs {
  const ev = events.filter((e) => e.campaignId === args.campaignId);
  const totals = {
    queued: 0, sent: 0, delivered: 0, failed: 0, undelivered: 0,
    inbound: 0, optOuts: 0,
  };
  const byLang: CampaignKPIs["byLanguage"] = {
    en: { sent: 0, delivered: 0, inbound: 0, optOuts: 0 },
    es: { sent: 0, delivered: 0, inbound: 0, optOuts: 0 },
  };
  const answerDist: CampaignKPIs["answerDistribution"] = {};
  const dropOff: Record<string, number> = {};
  const respondedContacts = new Set<string>();

  for (const e of ev) {
    if (e.direction === "outbound") {
      if (e.status && (e.status in totals)) totals[e.status] += 1;
      if (e.status === "sent") byLang[e.language].sent += 1;
      if (e.status === "delivered") byLang[e.language].delivered += 1;
    } else {
      totals.inbound += 1;
      byLang[e.language].inbound += 1;
      respondedContacts.add(e.contactId);
      if (e.optOut) {
        totals.optOuts += 1;
        byLang[e.language].optOuts += 1;
      }
      if (e.answerKey && e.answerValue) {
        const bucket = (answerDist[e.answerKey] ??= {});
        bucket[e.answerValue] = (bucket[e.answerValue] ?? 0) + 1;
      }
    }
    if (e.flowNodeId) {
      dropOff[e.flowNodeId] = (dropOff[e.flowNodeId] ?? 0) + 1;
    }
  }

  const outboundOutcomes = totals.sent + totals.delivered + totals.failed + totals.undelivered;
  return {
    campaignId: args.campaignId,
    totals,
    rates: {
      deliveryRate: outboundOutcomes ? totals.delivered / outboundOutcomes : 0,
      responseRate: totals.delivered ? respondedContacts.size / totals.delivered : 0,
      optOutRate: totals.delivered ? totals.optOuts / totals.delivered : 0,
      completionRate: args.startedConversations ? args.completedConversations / args.startedConversations : 0,
    },
    byLanguage: byLang,
    answerDistribution: answerDist,
    flowDropOff: dropOff,
  };
}
